import React from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Typography,
} from '@mui/material';
import {
  BarChart,
  CheckCircle,
  Edit,
  GetApp,
  Refresh,
  Schedule,
  TrendingUp as TrendingUpIcon,
  Visibility,
} from '@mui/icons-material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { formatToEST } from './postUtils';
import { PLATFORM_PERF_IDS } from './constants';

export default function PostsAnalyticsTab({ posts, onRefresh }) {
  return (
          <Box>
            {/* Analytics Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 4,
              }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: '#f0f6fc',
                    fontWeight: 600,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <BarChart sx={{ fontSize: 32, color: '#58a6ff' }} />
                  Analytics Dashboard
                </Typography>
                <Typography variant="body1" sx={{ color: '#7d8590' }}>
                  Track your post performance and optimize your content strategy
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={onRefresh}
                  sx={{
                    color: '#58a6ff',
                    borderColor: '#58a6ff',
                    '&:hover': {
                      borderColor: '#1f6feb',
                      bgcolor: 'rgba(88, 166, 255, 0.1)',
                    },
                  }}
                >
                  Refresh Data
                </Button>
                <Button
                  variant="contained"
                  startIcon={<GetApp />}
                  sx={{
                    bgcolor: '#238636',
                    '&:hover': { bgcolor: '#2ea043' },
                  }}
                >
                  Export Report
                </Button>
              </Box>
            </Box>

            {/* Analytics Stats Cards */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 3,
                flexWrap: 'wrap',
                justifyContent: 'space-between',
              }}
            >
              <Card
                sx={{
                  flex: '1 1 200px',
                  minWidth: '200px',
                  p: 2,
                  bgcolor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                  background:
                    'linear-gradient(135deg, #21262d 0%, #1a1a1a 100%)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      background:
                        'linear-gradient(135deg, #58a6ff 0%, #1f6feb 100%)',
                    }}
                  >
                    <Visibility sx={{ color: '#ffffff', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: '#f0f6fc', fontWeight: 600, lineHeight: 1 }}
                    >
                      {posts.filter((p) => p.status === 'published').length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#7d8590', fontSize: '0.75rem' }}
                    >
                      Published
                    </Typography>
                  </Box>
                </Box>
              </Card>

              <Card
                sx={{
                  flex: '1 1 200px',
                  minWidth: '200px',
                  p: 2,
                  bgcolor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                  background:
                    'linear-gradient(135deg, #21262d 0%, #1a1a1a 100%)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      background:
                        'linear-gradient(135deg, #238636 0%, #2ea043 100%)',
                    }}
                  >
                    <TrendingUpIcon sx={{ color: '#ffffff', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: '#f0f6fc', fontWeight: 600, lineHeight: 1 }}
                    >
                      {Math.round(
                        (posts.filter((p) => p.status === 'published').length /
                          Math.max(posts.length, 1)) *
                          100,
                      )}
                      %
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#7d8590', fontSize: '0.75rem' }}
                    >
                      Success Rate
                    </Typography>
                  </Box>
                </Box>
              </Card>

              <Card
                sx={{
                  flex: '1 1 200px',
                  minWidth: '200px',
                  p: 2,
                  bgcolor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                  background:
                    'linear-gradient(135deg, #21262d 0%, #1a1a1a 100%)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      background:
                        'linear-gradient(135deg, #f78166 0%, #ff6b35 100%)',
                    }}
                  >
                    <Schedule sx={{ color: '#ffffff', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: '#f0f6fc', fontWeight: 600, lineHeight: 1 }}
                    >
                      {posts.filter((p) => p.status === 'scheduled').length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#7d8590', fontSize: '0.75rem' }}
                    >
                      Scheduled
                    </Typography>
                  </Box>
                </Box>
              </Card>

              <Card
                sx={{
                  flex: '1 1 200px',
                  minWidth: '200px',
                  p: 2,
                  bgcolor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                  background:
                    'linear-gradient(135deg, #21262d 0%, #1a1a1a 100%)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      background:
                        'linear-gradient(135deg, #f85149 0%, #da3633 100%)',
                    }}
                  >
                    <ErrorOutlineIcon sx={{ color: '#ffffff', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: '#f0f6fc', fontWeight: 600, lineHeight: 1 }}
                    >
                      {posts.filter((p) => p.status === 'failed').length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#7d8590', fontSize: '0.75rem' }}
                    >
                      Failed
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Box>

            {/* Performance Charts */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 3,
                flexWrap: 'wrap',
              }}
            >
              {/* Platform Performance */}
              <Card
                sx={{
                  flex: '1 1 300px',
                  minWidth: '300px',
                  p: 2,
                  bgcolor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: '#f0f6fc',
                    mb: 2,
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  Platform Performance
                </Typography>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                >
                  {PLATFORM_PERF_IDS.map((platform) => {
                    const platformPosts = posts.filter(
                      (p) => p.platform === platform,
                    );
                    const publishedCount = platformPosts.filter(
                      (p) => p.status === 'published',
                    ).length;
                    const totalCount = platformPosts.length;
                    const percentage =
                      totalCount > 0
                        ? Math.round((publishedCount / totalCount) * 100)
                        : 0;

                    return (
                      <Box key={platform}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#f0f6fc',
                              textTransform: 'capitalize',
                              fontSize: '0.875rem',
                            }}
                          >
                            {platform}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: '#7d8590', fontSize: '0.75rem' }}
                          >
                            {publishedCount}/{totalCount} ({percentage}%)
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            height: 6,
                            bgcolor: '#30363d',
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: `${percentage}%`,
                              background:
                                'linear-gradient(90deg, #58a6ff 0%, #1f6feb 100%)',
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Card>

              {/* Content Type Performance */}
              <Card
                sx={{
                  flex: '1 1 300px',
                  minWidth: '300px',
                  p: 2,
                  bgcolor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: '#f0f6fc',
                    mb: 2,
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  Content Type Performance
                </Typography>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                >
                  {['image', 'video', 'text', 'carousel', 'story'].map(
                    (contentType) => {
                      const typePosts = posts.filter(
                        (p) => p.content_type === contentType,
                      );
                      const publishedCount = typePosts.filter(
                        (p) => p.status === 'published',
                      ).length;
                      const totalCount = typePosts.length;
                      const percentage =
                        totalCount > 0
                          ? Math.round((publishedCount / totalCount) * 100)
                          : 0;

                      return (
                        <Box key={contentType}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#f0f6fc',
                                textTransform: 'capitalize',
                                fontSize: '0.875rem',
                              }}
                            >
                              {contentType}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: '#7d8590', fontSize: '0.75rem' }}
                            >
                              {publishedCount}/{totalCount} ({percentage}%)
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              height: 6,
                              bgcolor: '#30363d',
                              borderRadius: 1,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                width: `${percentage}%`,
                                background:
                                  'linear-gradient(90deg, #238636 0%, #2ea043 100%)',
                                transition: 'width 0.3s ease',
                              }}
                            />
                          </Box>
                        </Box>
                      );
                    },
                  )}
                </Box>
              </Card>
            </Box>

            {/* Top Performers and Recent Activity */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              {/* Best Performing Posts */}
              <Card
                sx={{
                  flex: '1 1 300px',
                  minWidth: '300px',
                  p: 2,
                  bgcolor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: '#f0f6fc',
                    mb: 2,
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  Top Performing Posts
                </Typography>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                >
                  {posts
                    .filter((p) => p.status === 'published')
                    .slice(0, 5)
                    .map((post, index) => (
                      <Box
                        key={post.id}
                        sx={{
                          p: 1.5,
                          bgcolor: '#1a1a1a',
                          border: '1px solid #30363d',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background:
                              'linear-gradient(135deg, #58a6ff 0%, #1f6feb 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#f0f6fc',
                              fontWeight: 500,
                              mb: 0.25,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '0.875rem',
                            }}
                          >
                            {post.title || 'Untitled Post'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#7d8590',
                              textTransform: 'capitalize',
                              fontSize: '0.75rem',
                            }}
                          >
                            {post.platform} • {post.content_type}
                          </Typography>
                        </Box>
                        <Chip
                          label="Published"
                          size="small"
                          sx={{
                            bgcolor: '#238636',
                            color: '#ffffff',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            height: 20,
                          }}
                        />
                      </Box>
                    ))}
                  {posts.filter((p) => p.status === 'published').length ===
                    0 && (
                    <Box
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        color: '#7d8590',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        No published posts yet. Start creating content to see
                        performance metrics!
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Card>

              {/* Recent Activity */}
              <Card
                sx={{
                  flex: '1 1 300px',
                  minWidth: '300px',
                  p: 2,
                  bgcolor: '#21262d',
                  border: '1px solid #30363d',
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: '#f0f6fc',
                    mb: 2,
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  Recent Activity
                </Typography>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                >
                  {posts
                    .sort(
                      (a, b) => new Date(b.updated_at) - new Date(a.updated_at),
                    )
                    .slice(0, 5)
                    .map((post) => (
                      <Box
                        key={post.id}
                        sx={{
                          p: 1.5,
                          bgcolor: '#1a1a1a',
                          border: '1px solid #30363d',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor:
                              post.status === 'published'
                                ? '#238636'
                                : post.status === 'failed'
                                  ? '#f85149'
                                  : post.status === 'scheduled'
                                    ? '#f78166'
                                    : '#7d8590',
                          }}
                        >
                          {post.status === 'published' && (
                            <CheckCircle
                              sx={{ color: '#ffffff', fontSize: 14 }}
                            />
                          )}
                          {post.status === 'failed' && (
                            <ErrorOutlineIcon
                              sx={{ color: '#ffffff', fontSize: 14 }}
                            />
                          )}
                          {post.status === 'scheduled' && (
                            <Schedule sx={{ color: '#ffffff', fontSize: 14 }} />
                          )}
                          {post.status === 'draft' && (
                            <Edit sx={{ color: '#ffffff', fontSize: 14 }} />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#f0f6fc',
                              fontWeight: 500,
                              mb: 0.25,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '0.875rem',
                            }}
                          >
                            {post.title || 'Untitled Post'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: '#7d8590', fontSize: '0.75rem' }}
                          >
                            {formatToEST(post.updated_at)} • {post.platform}
                          </Typography>
                        </Box>
                        <Chip
                          label={post.status}
                          size="small"
                          sx={{
                            bgcolor:
                              post.status === 'published'
                                ? '#238636'
                                : post.status === 'failed'
                                  ? '#f85149'
                                  : post.status === 'scheduled'
                                    ? '#f78166'
                                    : '#7d8590',
                            color: '#ffffff',
                            fontWeight: 500,
                            textTransform: 'capitalize',
                            fontSize: '0.75rem',
                            height: 20,
                          }}
                        />
                      </Box>
                    ))}
                </Box>
              </Card>
            </Box>
          </Box>
  );
}
